// The lever (docs/04 §6, docs/07 §2): drag the knob down; ≥90% travel within 250 ms is a
// perfect pull. Release snaps back with overshoot. Space/button pulls animate it too.
import { Container, Sprite, type FederatedPointerEvent } from 'pixi.js';
import { B } from '../../content/balance';
import { getAtlas } from '../../art/rasterize';
import { audio } from '../../audio/audio';
import { clock } from '../timeline/clock';

const ZOOM = 2;

export class Lever extends Container {
  private knob: Sprite;
  private shaft: Sprite;
  private base: Sprite;
  private travel: number;
  private t = 0; // 0 = up, 1 = fully pulled
  private dragging = false;
  private grabY = 0;
  private grabT0 = 0;
  private reachedAt = 0;
  private fired = false;
  private ratchetStep = 0;
  private idleWiggle = 0;
  /** The Hand (docs/04 §21): a skeletal hand that grips the knob while autospin runs. */
  private hand: Sprite;
  private handDrop = 1;
  enabled = true;
  onPull: (perfect: boolean) => void = () => {};

  constructor() {
    super();
    const a = getAtlas();
    this.base = new Sprite(a.tex('lever_base'));
    this.shaft = new Sprite(a.tex('lever_shaft'));
    this.knob = new Sprite(a.tex('lever_knob'));
    for (const s of [this.base, this.shaft, this.knob]) s.scale.set(ZOOM);
    this.base.anchor.set(0.5);
    this.shaft.anchor.set(0.5, 1);
    this.knob.anchor.set(0.5);
    this.travel = this.shaft.height * 1.5;
    this.hand = new Sprite(a.tex('hand_bone'));
    this.hand.anchor.set(0.5, 1);
    this.hand.scale.set(ZOOM);
    this.hand.visible = false;
    this.hand.eventMode = 'none';
    this.addChild(this.shaft, this.base, this.knob, this.hand);
    this.knob.eventMode = 'static';
    this.knob.cursor = 'grab';
    this.knob.on('pointerdown', this.down);
    this.layout();
  }

  /** The Maw's-eye knob, unlocked by BECOME THE MAW (docs/06 §7). */
  setSkin(maw: boolean): void {
    const id = maw ? 'lever_knob_maw' : 'lever_knob';
    const tex = getAtlas().tex(id);
    if (this.knob.texture !== tex) this.knob.texture = tex;
  }

  /** Height from the knob's top position to its lowest position. */
  get trackHeight(): number {
    return this.travel + this.knob.height;
  }

  private layout(): void {
    // pivot (base) sits at the middle of the travel; the knob moves from above it to below it.
    const pivotY = 0;
    const top = -this.shaft.height;
    const knobY = top + this.t * this.travel;
    this.base.position.set(0, pivotY);
    this.knob.position.set(0, knobY + Math.sin(this.idleWiggle) * 1.5);
    const len = pivotY - this.knob.y;
    this.shaft.position.set(0, pivotY);
    this.shaft.scale.y = len / this.shaft.texture.height || 0.01;
    this.shaft.visible = Math.abs(len) > 2;
    this.hand.position.set(0, this.knob.y + 6 - this.handDrop * 200);
  }

  private down = (e: FederatedPointerEvent) => {
    if (!this.enabled) return;
    audio.play('leverGrab');
    this.dragging = true;
    this.fired = false;
    this.grabY = e.global.y;
    this.grabT0 = performance.now();
    this.reachedAt = 0;
    this.ratchetStep = 0;
    this.knob.cursor = 'grabbing';
    this.knob.scale.set(ZOOM * 1.1);
    // globalpointermove reaches the (static) knob even when the pointer leaves it.
    const target = this.knob;
    const move = (ev: FederatedPointerEvent) => this.move(ev);
    const up = () => {
      target.off('globalpointermove', move);
      window.removeEventListener('pointerup', up);
      this.up();
    };
    target.on('globalpointermove', move);
    window.addEventListener('pointerup', up);
  };

  private move(e: FederatedPointerEvent): void {
    if (!this.dragging) return;
    const t = Math.max(0, Math.min(1, (e.global.y - this.grabY) / this.travel));
    this.t = t;
    const step = Math.floor(t * 6);
    if (step > this.ratchetStep) {
      this.ratchetStep = step;
      audio.play('ratchet', { pitch: step });
    }
    if (t >= B.PERFECT_PULL_TRAVEL && !this.reachedAt) this.reachedAt = performance.now();
    if (t >= 0.98 && !this.fired) this.release(true);
    this.layout();
  }

  private up(): void {
    if (!this.dragging) return;
    if (!this.fired && this.t >= 0.6) this.release(true);
    else this.release(false);
  }

  private release(fire: boolean): void {
    this.dragging = false;
    this.knob.cursor = 'grab';
    this.knob.scale.set(ZOOM);
    if (fire && !this.fired) {
      this.fired = true;
      const perfect = this.reachedAt > 0 && this.reachedAt - this.grabT0 <= B.PERFECT_PULL_MS;
      audio.play(perfect ? 'perfect' : 'clack');
      this.onPull(perfect);
    }
    this.springBack();
  }

  private springBack(): void {
    clock.fx(this, 150, { t: 0, ease: 'back.out(2.5)', onUpdate: () => this.layout() });
  }

  /** Animate a full pull for keyboard/button input. */
  async animatePull(): Promise<void> {
    audio.play('clack');
    await clock.tween(this, 90, { t: 1, ease: 'power2.in', onUpdate: () => this.layout() });
    this.springBack();
  }

  /** Lower (or raise) the Hand onto the knob. */
  async setHand(on: boolean): Promise<void> {
    if (on === this.hand.visible && (on ? this.handDrop === 0 : true)) return;
    if (on) {
      this.hand.visible = true;
      await clock.tween(this, 450, { handDrop: 0, ease: 'back.out(1.2)', onUpdate: () => this.layout() });
    } else {
      await clock.tween(this, 350, { handDrop: 1, ease: 'power2.in', onUpdate: () => this.layout() });
      this.hand.visible = false;
    }
  }

  get handOn(): boolean {
    return this.hand.visible && this.handDrop === 0;
  }

  flashPerfect(): void {
    this.knob.tint = 0xffffff;
  }

  wiggle(dt: number, active: boolean): void {
    if (!active || this.dragging) {
      if (this.idleWiggle) {
        this.idleWiggle = 0;
        this.layout();
      }
      return;
    }
    this.idleWiggle += dt * 0.006;
    this.layout();
  }
}
